import React, { useRef, useState, useEffect } from "react";
import { Check, Phone } from "lucide-react";

import { acceptPublicProposal, requestProposalCallback } from "../api/prospectingReportsApi";

/**
 * The vendor's accept / request-callback panel, shown inside the proposal's
 * call-to-action on the public page. Acceptance is an engagement signal (it
 * raises a follow-up for the agent); it is not a sales authority.
 *
 * Self-contained signature pad (canvas) so there is no dependency on the e-sign
 * modal here.
 */
const SignaturePad = ({ canvasRef }) => {
  const drawing = useRef(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#1b2740";

    const pos = (e) => {
      const r = canvas.getBoundingClientRect();
      const t = e.touches ? e.touches[0] : e;
      return { x: t.clientX - r.left, y: t.clientY - r.top };
    };
    const start = (e) => { drawing.current = true; const p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); e.preventDefault(); };
    const move = (e) => { if (!drawing.current) return; const p = pos(e); ctx.lineTo(p.x, p.y); ctx.stroke(); e.preventDefault(); };
    const end = () => { drawing.current = false; };

    canvas.addEventListener("mousedown", start);
    canvas.addEventListener("mousemove", move);
    window.addEventListener("mouseup", end);
    canvas.addEventListener("touchstart", start, { passive: false });
    canvas.addEventListener("touchmove", move, { passive: false });
    window.addEventListener("touchend", end);
    return () => {
      canvas.removeEventListener("mousedown", start);
      canvas.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", end);
      canvas.removeEventListener("touchstart", start);
      canvas.removeEventListener("touchmove", move);
      window.removeEventListener("touchend", end);
    };
  }, [canvasRef]);

  return (
    <canvas ref={canvasRef} width={340} height={110}
      className="w-full max-w-sm bg-white rounded-lg border border-gray-200 touch-none" />
  );
};

const AcceptProposalPanel = ({ token, block = {}, onAccepted, requiredApprovals = 1, approvalsCount = 0, requiresEmail = false }) => {
  const canvasRef = useRef(null);
  const [name, setName] = useState("");
  const [agree, setAgree] = useState(false);
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState("");
  const [progress, setProgress] = useState(null);
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const multi = (requiredApprovals || 1) > 1;

  const showAccept = block.show_accept !== false;
  const showCallback = !!block.show_callback;

  const clearSig = () => {
    const c = canvasRef.current;
    if (c) c.getContext("2d").clearRect(0, 0, c.width, c.height);
  };

  const accept = async () => {
    setError("");
    if (!name.trim() || !agree) {
      setError("Please enter your name and tick to agree.");
      return;
    }
    if (requiresEmail && !email.trim()) {
      setError("Please enter your email so each owner is recorded separately.");
      return;
    }
    setBusy(true);
    try {
      const signature = canvasRef.current ? canvasRef.current.toDataURL("image/png") : "";
      const { data } = await acceptPublicProposal(token, {
        name: name.trim(), email: email.trim(), agree: true, signature,
      });
      if (data?.fully_accepted) {
        setDone("accepted");
        onAccepted && onAccepted();
      } else {
        setProgress({ count: data?.approvals_count || 0, required: data?.required_approvals || requiredApprovals });
        setDone("partial");
      }
    } catch (e) {
      setError("Sorry, we couldn't record that. Please try again or contact your agent.");
    } finally {
      setBusy(false);
    }
  };

  const callback = async () => {
    setError("");
    setBusy(true);
    try {
      await requestProposalCallback(token, { name: name.trim(), phone: phone.trim() });
      setDone("callback");
    } catch (e) {
      setError("Sorry, we couldn't send that request. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (done === "accepted") {
    return (
      <div className="mt-4 rounded-lg bg-white/15 p-4 text-white">
        <Check className="inline w-5 h-5 mr-1" /> Thank you — your agent has been notified.
      </div>
    );
  }
  if (done === "callback") {
    return (
      <div className="mt-4 rounded-lg bg-white/15 p-4 text-white">
        <Phone className="inline w-5 h-5 mr-1" /> Thanks — your agent will call you back shortly.
      </div>
    );
  }
  if (done === "partial") {
    return (
      <div className="mt-4 rounded-lg bg-white/15 p-4 text-white">
        <Check className="inline w-5 h-5 mr-1" /> Thank you — your acceptance is recorded.
        {progress ? ` ${progress.count} of ${progress.required} owners have accepted; we'll proceed once everyone has.` : ""}
      </div>
    );
  }

  return (
    <div className="mt-4 rounded-lg bg-white p-4 text-[#1b2740]">
      <label className="block text-xs font-semibold text-gray-500 mb-1">Your name</label>
      <input className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm mb-3"
        value={name} onChange={(e) => setName(e.target.value)} />

      {requiresEmail && (
        <>
          <label className="block text-xs font-semibold text-gray-500 mb-1">Your email</label>
          <input type="email" className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm mb-1"
            value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="The email your agent has on file" />
          {multi && (
            <p className="text-xs text-gray-500 mb-3">
              All {requiredApprovals} owners need to accept{approvalsCount ? ` — ${approvalsCount} so far` : ""}.
            </p>
          )}
          {!multi && <div className="mb-3" />}
        </>
      )}

      {showAccept && (
        <>
          <label className="block text-xs font-semibold text-gray-500 mb-1">Signature (optional)</label>
          <SignaturePad canvasRef={canvasRef} />
          <button type="button" onClick={clearSig} className="text-xs text-gray-400 mb-3 mt-1">Clear</button>
          <label className="flex items-start gap-2 text-sm text-gray-700 mb-3">
            <input type="checkbox" className="mt-1" checked={agree} onChange={(e) => setAgree(e.target.checked)} />
            <span>I'm happy to proceed with this proposal. I understand this is not a formal
              sales authority and my agent will prepare that separately.</span>
          </label>
        </>
      )}

      {error && <p className="text-sm text-red-600 mb-2">{error}</p>}

      <div className="flex flex-wrap gap-2">
        {showAccept && (
          <button type="button" onClick={accept} disabled={busy}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#0f8d9b] text-white text-sm font-semibold disabled:opacity-50">
            <Check className="w-4 h-4" /> {block.accept_label || "Accept proposal"}
          </button>
        )}
        {showCallback && (
          <button type="button" onClick={callback} disabled={busy}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg border border-gray-200 text-sm font-semibold text-gray-700 disabled:opacity-50">
            <Phone className="w-4 h-4" /> Request a callback
          </button>
        )}
      </div>
    </div>
  );
};

export default AcceptProposalPanel;
